import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
  Heart, MessageSquare, Calendar, Star, ShoppingBag, BookOpen,
  ClipboardList, Bell, Send, CheckCircle, Clock, User, Shield,
  Eye, Edit3, AlertTriangle, Plus, ChevronRight, X, Phone,
  Mail, Lock, CreditCard, Camera, ThumbsUp, ThumbsDown,
  FileText, Home, Search, Filter, MapPin, DollarSign, Smile,
  Target, HelpCircle, MoreHorizontal, RefreshCw, ChevronLeft, Info
} from 'lucide-react';
import FamilyPortalService from '../../services/portal/FamilyPortalService';
import FamilyNotificationService from '../../services/portal/FamilyNotificationService';
import { CardSkeleton, ListSkeleton } from '../Common/SkeletonLoaders';
import { useToast } from '../Common/ToastProvider';
import { Tooltip, Modal } from '../Common/ReusableUI';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'activities', label: 'Activities', icon: Calendar },
  { id: 'visits', label: 'Visit Scheduling', icon: Calendar },
  { id: 'careplan', label: 'Care Plan', icon: Target },
  { id: 'services', label: 'Services', icon: ShoppingBag },
  { id: 'lifestory', label: 'Life Story', icon: BookOpen },
  { id: 'feedback', label: 'Feedback', icon: ClipboardList },
  { id: 'surveys', label: 'Surveys', icon: Star },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'permissions', label: 'Permissions', icon: Shield },
];

const MOBILE_NAV_ITEMS = [
  { id: 'overview', label: 'Home', icon: Home },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'visits', label: 'Visits', icon: Calendar },
  { id: 'careplan', label: 'Care Plan', icon: Target },
];

const FamilyPortal = () => {
  const { state } = useAppContext();
  const { deviceInfo } = state;
  const { optimization } = deviceInfo;
  const { showToast } = useToast();

  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [portalData, setPortalData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const mockFamilyUser = {
    id: 'FAM-demo-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    relationship: 'Daughter',
    residentId: 'res-001',
    facilityId: 'sunshine',
    permissionLevel: 'full',
    mfaEnabled: true,
    preferences: { emailNotifications: true, pushNotifications: false },
  };

  const loadPortalData = useCallback(async () => {
    setLoading(true);
    try {
      const viewResult = await FamilyPortalService.getResidentPortalView(mockFamilyUser.id);
      if (viewResult.success) setPortalData(viewResult.data);

      const notifResult = await FamilyNotificationService.getNotifications(mockFamilyUser.id, { limit: 20 });
      if (notifResult.success) setNotifications(notifResult.data);

      const countResult = await FamilyNotificationService.getUnreadCount(mockFamilyUser.id);
      if (countResult.success) setUnreadCount(countResult.count);
    } catch (err) {
      console.error('[FamilyPortal] Failed to load portal data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPortalData(); }, [loadPortalData]);

  const renderDesktopTabs = () => (
    <div
      className="flex gap-1 overflow-x-auto pb-2 mb-6 border-b border-gray-200"
      role="tablist"
      aria-label="Family Portal navigation"
    >
      {TABS.map(tab => {
        const Icon = tab.icon;
        const isActive = activeSubTab === tab.id;
        return (
          <button
            key={tab.id}
            role="tab"
            id={`fp-tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`fp-tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
              isActive
                ? 'bg-pink-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-pink-50 hover:text-pink-600'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className={optimization.fontSize}>{tab.label}</span>
            {tab.id === 'notifications' && unreadCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">{unreadCount}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading Family Portal">
        <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-6 h-32 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}
        </div>
        <ListSkeleton count={4} />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isMobile ? 'pb-24' : ''}`}>
      {/* Portal Header */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className={`${optimization.fontSize === 'text-sm' ? 'text-2xl' : 'text-3xl'} font-bold flex items-center gap-3`}>
              Family Portal
              <Heart className={`${optimization.iconSize} text-pink-300`} />
            </h1>
            <p className="mt-1 opacity-90">Welcome back, {mockFamilyUser.name}</p>
            <div className="flex items-center gap-4 mt-3 text-sm opacity-80">
              <span className="flex items-center gap-1"><User className="w-4 h-4" /> {mockFamilyUser.relationship}</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> {mockFamilyUser.permissionLevel} access</span>
              {mockFamilyUser.mfaEnabled && <span className="flex items-center gap-1"><Lock className="w-4 h-4" /> MFA enabled</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl hover:bg-white/30 transition flex items-center gap-2">
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && <span className="text-sm font-semibold">{unreadCount}</span>}
            </button>
            <button className="bg-white text-pink-600 px-4 py-2 rounded-xl font-semibold hover:bg-pink-50 transition flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Message
            </button>
          </div>
        </div>
      </div>

      {!isMobile && renderDesktopTabs()}

      <div
        role="tabpanel"
        id={`fp-tabpanel-${activeSubTab}`}
        aria-labelledby={`fp-tab-${activeSubTab}`}
      >
        {activeSubTab === 'overview' && <OverviewPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'messages' && <MessagesPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'activities' && <ActivitiesPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'visits' && <VisitSchedulingPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'careplan' && <CarePlanPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'services' && <ServicesPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'lifestory' && <LifeStoryPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'feedback' && <FeedbackPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'surveys' && <SurveysPanel user={mockFamilyUser} optimization={optimization} />}
        {activeSubTab === 'notifications' && <NotificationsPanel user={mockFamilyUser} notifications={notifications} onRefresh={loadPortalData} optimization={optimization} />}
        {activeSubTab === 'permissions' && <PermissionsPanel user={mockFamilyUser} optimization={optimization} />}
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <>
          {showMoreMenu && (
            <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowMoreMenu(false)} />
          )}
          <nav
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
            aria-label="Mobile navigation"
          >
            {showMoreMenu && (
              <div className="border-b border-gray-100 p-3 grid grid-cols-4 gap-2 bg-white animate-[slideUp_0.2s_ease-out]">
                {TABS.filter(t => !MOBILE_NAV_ITEMS.some(m => m.id === t.id)).map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => { setActiveSubTab(tab.id); setShowMoreMenu(false); }}
                      className={`flex flex-col items-center justify-center min-w-[44px] min-h-[44px] p-2 rounded-xl transition-colors ${
                        activeSubTab === tab.id ? 'bg-pink-50 text-pink-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-[10px] mt-1 font-medium text-center leading-tight">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex justify-around items-center px-1 py-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))]">
              {MOBILE_NAV_ITEMS.map(item => {
                const Icon = item.icon;
                const isActive = activeSubTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => { setActiveSubTab(item.id); setShowMoreMenu(false); }}
                    className={`relative flex flex-col items-center justify-center min-w-[44px] min-h-[44px] px-3 py-1 rounded-xl transition-colors ${
                      isActive ? 'text-pink-600' : 'text-gray-400'
                    }`}
                    aria-label={item.label}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
                    {item.id === 'messages' && unreadCount > 0 && (
                      <span className="absolute top-0 right-1 min-w-[16px] h-4 text-[9px] rounded-full bg-red-500 text-white flex items-center justify-center px-1">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
              <button
                onClick={() => setShowMoreMenu(prev => !prev)}
                className={`relative flex flex-col items-center justify-center min-w-[44px] min-h-[44px] px-3 py-1 rounded-xl transition-colors ${
                  showMoreMenu ? 'text-pink-600' : 'text-gray-400'
                }`}
                aria-label="More options"
                aria-expanded={showMoreMenu}
              >
                <MoreHorizontal className="w-6 h-6" />
                <span className="text-[10px] mt-0.5 font-medium">More</span>
              </button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
};

// ─── Overview Panel ───────────────────────────────────────────────────

const OverviewPanel = ({ user, optimization }) => {
  const stats = [
    { label: 'Unread Messages', value: 3, icon: MessageSquare, color: 'blue' },
    { label: 'Upcoming Activities', value: 5, icon: Calendar, color: 'green' },
    { label: 'Care Plan Updates', value: 2, icon: ClipboardList, color: 'purple' },
    { label: 'Photos This Week', value: 8, icon: Camera, color: 'orange' },
  ];

  const recentUpdates = [
    { id: 1, type: 'care', title: 'Care Plan Review Completed', desc: 'Quarterly review shows positive progress in mobility goals.', time: '2 hours ago', icon: CheckCircle, color: 'green' },
    { id: 2, type: 'activity', title: 'Morning Art Class', desc: 'Participated in watercolour painting. Seemed to enjoy the session.', time: '4 hours ago', icon: Star, color: 'yellow' },
    { id: 3, type: 'alert', title: 'Dietary Adjustment', desc: 'Slight change to meal texture based on speech pathology review.', time: '1 day ago', icon: AlertTriangle, color: 'orange' },
    { id: 4, type: 'message', title: 'Message from Nurse Kim', desc: 'Your mother had a wonderful day today and was very chatty at lunch.', time: '1 day ago', icon: MessageSquare, color: 'blue' },
  ];

  return (
    <div className="space-y-6">
      {/* Resident Summary Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-100 to-rose-200 flex items-center justify-center text-3xl shrink-0">
            👵
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">Margaret Johnson</h2>
            <p className="text-gray-500 mt-1">Room 12A — Wattle Wing</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium">Care Level: Medium</span>
              <span className="px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">Mobility: Walking Frame</span>
              <span className="px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-700 font-medium">Diet: Soft Texture</span>
            </div>
            <p className="text-sm text-gray-600 mt-3">
              Enjoys gardening, reading, and spending time with family. Prefers to be called "Maggie".
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-${s.color}-100 rounded-lg`}>
                  <Icon className={`w-5 h-5 text-${s.color}-600`} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{s.value}</h3>
                  <p className="text-xs text-gray-600">{s.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Updates */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Recent Updates</h2>
          <div className="space-y-4">
            {recentUpdates.map(update => {
              const Icon = update.icon;
              return (
                <div key={update.id} className="flex items-start gap-4 p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className={`p-2 bg-${update.color}-100 rounded-lg shrink-0`}>
                    <Icon className={`w-5 h-5 text-${update.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900">{update.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{update.desc}</p>
                    <span className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" />{update.time}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 shrink-0" />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Photo Gallery Preview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Recent Photos</h2>
          <button className="text-pink-600 text-sm font-medium hover:underline">View All</button>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
          {['🎨', '🌻', '🎂', '🎵', '📚', '🧶'].map((emoji, i) => (
            <div key={i} className="aspect-square rounded-lg bg-gradient-to-br from-pink-50 to-rose-100 flex items-center justify-center text-2xl hover:scale-105 transition-transform cursor-pointer">
              {emoji}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Messages Panel ───────────────────────────────────────────────────

const MessagesPanel = ({ user, optimization }) => {
  const { showToast } = useToast();
  const [messages] = useState([
    { id: 1, from: 'Nurse Kim', fromType: 'staff', subject: 'Daily Update', body: 'Maggie had a great day! She participated in morning exercises and enjoyed the garden walk. She ate well at lunch — roast chicken, which is her favourite.', time: '2 hours ago', read: false },
    { id: 2, from: 'Activity Coordinator Jess', fromType: 'staff', subject: 'Art Class Photos', body: 'Sharing some lovely photos from today\'s watercolour class. Maggie painted a beautiful landscape!', time: '5 hours ago', read: true },
    { id: 3, from: 'Dr. Patel', fromType: 'staff', subject: 'Quarterly Review Summary', body: 'The quarterly care review has been completed. Overall, Maggie is progressing well. Minor dietary adjustments recommended.', time: '1 day ago', read: true },
    { id: 4, from: 'You', fromType: 'family', subject: 'Visit Scheduling', body: 'Hi, I\'d like to arrange a visit this Saturday afternoon. Could you let me know a good time?', time: '2 days ago', read: true },
  ]);
  const [showCompose, setShowCompose] = useState(false);
  const [newMessage, setNewMessage] = useState({ to: '', subject: '', body: '' });

  const handleSend = async () => {
    if (!newMessage.body.trim()) return;
    await FamilyPortalService.sendMessage(user.id, {
      residentId: user.residentId,
      subject: newMessage.subject,
      body: newMessage.body,
      toType: 'care_staff',
    });
    setShowCompose(false);
    setNewMessage({ to: '', subject: '', body: '' });
    showToast('Message sent successfully!', 'success');
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Messages — Care Circle</h2>
        <button onClick={() => setShowCompose(true)} className="bg-pink-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-pink-700 transition flex items-center gap-2">
          <Plus className="w-4 h-4" /> Compose
        </button>
      </div>

      {showCompose && (
        <div className="bg-white rounded-xl shadow-lg border-2 border-pink-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">New Message</h3>
            <button onClick={() => setShowCompose(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">To</label>
              <select value={newMessage.to} onChange={e => setNewMessage(p => ({ ...p, to: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent">
                <option value="">Select recipient...</option>
                <option value="care_team">Care Team</option>
                <option value="nurse">Nurse in Charge</option>
                <option value="manager">Facility Manager</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <input type="text" value={newMessage.subject} onChange={e => setNewMessage(p => ({ ...p, subject: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent" placeholder="Message subject..." />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <textarea rows={4} value={newMessage.body} onChange={e => setNewMessage(p => ({ ...p, body: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none" placeholder="Type your message..." />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCompose(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSend} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700 flex items-center gap-2">
                <Send className="w-4 h-4" /> Send
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
        {messages.map(msg => (
          <div key={msg.id} className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${!msg.read ? 'bg-pink-50/50' : ''}`}>
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full shrink-0 ${msg.fromType === 'family' ? 'bg-pink-100' : 'bg-blue-100'}`}>
                {msg.fromType === 'family' ? <User className="w-4 h-4 text-pink-600" /> : <User className="w-4 h-4 text-blue-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{msg.from}</span>
                  {!msg.read && <span className="w-2 h-2 rounded-full bg-pink-500" />}
                </div>
                <h4 className="font-medium text-sm text-gray-700 mt-0.5">{msg.subject}</h4>
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{msg.body}</p>
                <span className="text-xs text-gray-400 mt-1 block">{msg.time}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Activities Panel ─────────────────────────────────────────────────

const ActivitiesPanel = ({ user, optimization }) => {
  const { showToast } = useToast();
  const [activities] = useState([
    { id: 1, name: 'Morning Tai Chi', date: '2026-04-11', time: '8:00 AM', location: 'Garden Courtyard', spots: 8, rsvpd: false, category: 'Exercise' },
    { id: 2, name: 'Music Therapy', date: '2026-04-11', time: '10:30 AM', location: 'Community Hall', spots: 12, rsvpd: true, category: 'Therapy' },
    { id: 3, name: 'Movie Afternoon — Classic Films', date: '2026-04-12', time: '2:00 PM', location: 'Lounge Room', spots: 20, rsvpd: false, category: 'Entertainment' },
    { id: 4, name: 'Gardening Club', date: '2026-04-13', time: '9:30 AM', location: 'Sensory Garden', spots: 6, rsvpd: true, category: 'Leisure' },
    { id: 5, name: 'Family BBQ Day', date: '2026-04-14', time: '12:00 PM', location: 'Outdoor Pavilion', spots: 30, rsvpd: false, category: 'Social' },
  ]);

  const handleRSVP = async (activityId, status) => {
    await FamilyPortalService.rsvpActivity(user.id, activityId, user.residentId, status);
    showToast(
      status === 'attending' ? 'RSVP confirmed! We look forward to seeing you.' : 'RSVP cancelled.',
      status === 'attending' ? 'success' : 'info'
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Activities & Events</h2>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
            <Filter className="w-4 h-4" /> Filter
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {activities.map(act => (
          <div key={act.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 font-medium">{act.category}</span>
                  {act.rsvpd && <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" />RSVP'd</span>}
                </div>
                <h3 className="font-semibold text-gray-900">{act.name}</h3>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {act.date}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {act.time}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {act.location}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{act.spots} spots available</p>
              </div>
              <button
                onClick={() => handleRSVP(act.id, act.rsvpd ? 'declined' : 'attending')}
                className={`px-4 py-2 rounded-lg font-medium transition shrink-0 ${
                  act.rsvpd
                    ? 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600'
                    : 'bg-pink-600 text-white hover:bg-pink-700'
                }`}
              >
                {act.rsvpd ? 'Cancel RSVP' : 'RSVP'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Extra Services Panel ─────────────────────────────────────────────

const ServicesPanel = ({ user, optimization }) => {
  const catalogue = FamilyPortalService.getServiceCatalogue();
  const [selectedService, setSelectedService] = useState(null);
  const [bookingDate, setBookingDate] = useState('');

  const handleBook = async () => {
    if (!selectedService || !bookingDate) return;
    const result = await FamilyPortalService.bookExtraService(user.id, {
      residentId: user.residentId,
      serviceId: selectedService.id,
      requestedDate: bookingDate,
    });
    if (result.success) {
      await FamilyPortalService.processStripePayment(result.data.id, 'mock_stripe_token');
      setSelectedService(null);
      setBookingDate('');
      console.info('[FamilyPortal] Service booked and paid');
    }
  };

  const categories = [...new Set(catalogue.map(s => s.category))];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Extra Services</h2>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CreditCard className="w-4 h-4" /> Secure payment via Stripe
        </div>
      </div>

      {categories.map(cat => (
        <div key={cat}>
          <h3 className="font-medium text-gray-700 mb-3">{cat}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {catalogue.filter(s => s.category === cat).map(svc => (
              <div
                key={svc.id}
                className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition-all ${
                  selectedService?.id === svc.id ? 'border-pink-500 shadow-md' : 'border-gray-200 hover:border-pink-300'
                }`}
                onClick={() => setSelectedService(svc)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-gray-900">{svc.name}</h4>
                    <p className="text-sm text-gray-500">{svc.frequency}</p>
                  </div>
                  <span className="text-lg font-bold text-pink-600">${svc.price.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {selectedService && (
        <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
          <h3 className="font-semibold mb-4">Book: {selectedService.name}</h3>
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">Preferred Date</label>
              <input type="date" value={bookingDate} onChange={e => setBookingDate(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500" />
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-1">Total: <span className="font-bold text-lg text-pink-600">${selectedService.price.toFixed(2)}</span></p>
              <button onClick={handleBook} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Book & Pay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Life Story Panel ─────────────────────────────────────────────────

const LifeStoryPanel = ({ user, optimization }) => {
  const [entries] = useState([
    { id: 1, category: 'childhood', decade: '1940s', title: 'Growing Up in Ballarat', content: 'Mum grew up on a dairy farm just outside Ballarat. She loved feeding the calves in the morning before school.', contributedBy: 'Sarah (Daughter)' },
    { id: 2, category: 'career', decade: '1960s', title: 'Teaching Career', content: 'She started teaching primary school at age 22. She taught at Ballarat North Primary for 15 years and was loved by her students.', contributedBy: 'James (Son)' },
    { id: 3, category: 'family', decade: '1970s', title: 'Raising a Family', content: 'Mum and Dad raised three children in their home in Kew. Sunday roasts were always a family tradition.', contributedBy: 'Sarah (Daughter)' },
  ]);
  const [showAdd, setShowAdd] = useState(false);
  const [newEntry, setNewEntry] = useState({ category: 'childhood', decade: '', title: '', content: '' });
  const categories = ['childhood', 'career', 'family', 'hobbies', 'travel', 'milestones', 'preferences'];

  const handleSubmit = async () => {
    if (!newEntry.title || !newEntry.content) return;
    await FamilyPortalService.addLifeStoryEntry(user.id, { ...newEntry, residentId: user.residentId });
    setShowAdd(false);
    setNewEntry({ category: 'childhood', decade: '', title: '', content: '' });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">My Life Story</h2>
          <p className="text-sm text-gray-500">Contribute memories and stories about your loved one</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="bg-pink-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-pink-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Story
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl shadow-lg border-2 border-pink-200 p-6">
          <h3 className="font-semibold mb-4">Add a Memory</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Category</label>
              <select value={newEntry.category} onChange={e => setNewEntry(p => ({ ...p, category: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg">
                {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Decade (optional)</label>
              <input type="text" value={newEntry.decade} onChange={e => setNewEntry(p => ({ ...p, decade: e.target.value }))} placeholder="e.g. 1960s" className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <input type="text" value={newEntry.title} onChange={e => setNewEntry(p => ({ ...p, title: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" placeholder="Give this memory a title..." />
          </div>
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700">Story</label>
            <textarea rows={4} value={newEntry.content} onChange={e => setNewEntry(p => ({ ...p, content: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg resize-none" placeholder="Share the story..." />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSubmit} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700">Submit</button>
          </div>
        </div>
      )}

      <div className="relative pl-8 border-l-2 border-pink-200 space-y-6">
        {entries.map(entry => (
          <div key={entry.id} className="relative">
            <div className="absolute -left-[2.35rem] top-2 w-4 h-4 rounded-full bg-pink-500 border-2 border-white" />
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 text-xs rounded-full bg-pink-100 text-pink-700 font-medium">{entry.category}</span>
                {entry.decade && <span className="text-xs text-gray-500">{entry.decade}</span>}
              </div>
              <h3 className="font-semibold text-gray-900">{entry.title}</h3>
              <p className="text-sm text-gray-600 mt-2">{entry.content}</p>
              <p className="text-xs text-gray-400 mt-3">Contributed by {entry.contributedBy}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Feedback / Complaints Panel ──────────────────────────────────────

const FeedbackPanel = ({ user, optimization }) => {
  const { showToast } = useToast();
  const [feedbackType, setFeedbackType] = useState('complaint');
  const [form, setForm] = useState({ category: '', subject: '', description: '', urgency: 'normal' });
  const [history] = useState([
    { id: 'FB-001', type: 'complaint', subject: 'Meal quality concern', status: 'resolved', date: '2026-03-20', resolution: 'Menu adjusted per dietary request.' },
    { id: 'FB-002', type: 'compliment', subject: 'Excellent care from Nurse Sarah', status: 'acknowledged', date: '2026-03-28', resolution: null },
  ]);

  const handleSubmit = async () => {
    if (!form.subject || !form.description) return;
    await FamilyPortalService.submitFeedback(user.id, { ...form, type: feedbackType, residentId: user.residentId });
    showToast('Thank you! Your feedback has been submitted and will be reviewed.', 'success');
    setForm({ category: '', subject: '', description: '', urgency: 'normal' });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Feedback & Complaints</h2>

      {/* Type Selector */}
      <div className="flex gap-3">
        {[
          { id: 'complaint', label: 'Complaint', icon: ThumbsDown, color: 'red' },
          { id: 'compliment', label: 'Compliment', icon: ThumbsUp, color: 'green' },
          { id: 'suggestion', label: 'Suggestion', icon: FileText, color: 'blue' },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setFeedbackType(t.id)}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                feedbackType === t.id ? `border-${t.color}-500 bg-${t.color}-50` : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Icon className={`w-6 h-6 mx-auto mb-2 text-${t.color}-600`} />
              <p className="text-sm font-medium text-center">{t.label}</p>
            </button>
          );
        })}
      </div>

      {/* Submission Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select category...</option>
                <option value="care_quality">Care Quality</option>
                <option value="meals">Meals & Nutrition</option>
                <option value="cleanliness">Cleanliness</option>
                <option value="communication">Communication</option>
                <option value="activities">Activities</option>
                <option value="staff">Staff</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Urgency</label>
              <select value={form.urgency} onChange={e => setForm(p => ({ ...p, urgency: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Subject</label>
            <input type="text" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" placeholder="Brief summary..." />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <textarea rows={4} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg resize-none" placeholder="Please describe in detail..." />
          </div>
          <div className="flex justify-end">
            <button onClick={handleSubmit} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700 flex items-center gap-2">
              <Send className="w-4 h-4" /> Submit Feedback
            </button>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold mb-4">Previous Feedback</h3>
        <div className="space-y-3">
          {history.map(h => (
            <div key={h.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    h.type === 'complaint' ? 'bg-red-100 text-red-700' : h.type === 'compliment' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                  }`}>{h.type}</span>
                  <span className="font-medium text-sm">{h.subject}</span>
                </div>
                {h.resolution && <p className="text-xs text-gray-500 mt-1">Resolution: {h.resolution}</p>}
              </div>
              <div className="text-right">
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  h.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>{h.status}</span>
                <p className="text-xs text-gray-400 mt-1">{h.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Surveys Panel ────────────────────────────────────────────────────

const SurveysPanel = ({ user, optimization }) => {
  const { showToast } = useToast();
  const templates = FamilyPortalService.getSurveyTemplates();
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [answers, setAnswers] = useState({});

  const handleSubmit = async () => {
    if (!activeSurvey) return;
    await FamilyPortalService.submitSurveyResponse(user.id, activeSurvey.id, answers);
    showToast('Thank you for completing the survey! Your responses have been recorded.', 'success');
    setActiveSurvey(null);
    setAnswers({});
  };

  const questionTooltip = (q) => {
    if (q.type === 'rating') return `Rate from 1 (poor) to ${q.scale} (excellent)`;
    if (q.type === 'nps') return 'How likely are you to recommend? 0 = not at all, 10 = definitely';
    return 'Share your thoughts in your own words';
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Satisfaction Surveys</h2>
      <p className="text-sm text-gray-500">Your feedback helps us improve care quality and feeds into our National Quality Indicator Program (NQIP) reporting.</p>

      {!activeSurvey ? (
        <div className="space-y-4">
          {templates.map(survey => (
            <div key={survey.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{survey.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{survey.description}</p>
                  <p className="text-xs text-gray-400 mt-2">{survey.questions.length} questions — Expires {new Date(survey.expiresAt).toLocaleDateString('en-AU')}</p>
                </div>
                <button onClick={() => setActiveSurvey(survey)} className="bg-pink-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-pink-700">
                  Start Survey
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-lg mb-6">{activeSurvey.title}</h3>
          <div className="space-y-6">
            {activeSurvey.questions.map((q, idx) => (
              <div key={q.id} className="border-b border-gray-100 pb-4 last:border-0">
                <label className="font-medium text-gray-800 flex items-center gap-2">
                  {idx + 1}. {q.text}
                  <Tooltip content={questionTooltip(q)}>
                    <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  </Tooltip>
                </label>
                {q.type === 'rating' && (
                  <div className="flex gap-2 mt-3">
                    {Array.from({ length: q.scale }, (_, i) => i + 1).map(val => (
                      <button
                        key={val}
                        onClick={() => setAnswers(p => ({ ...p, [q.id]: val }))}
                        className={`w-10 h-10 rounded-lg font-medium transition ${
                          answers[q.id] === val ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-pink-100'
                        }`}
                      >{val}</button>
                    ))}
                  </div>
                )}
                {q.type === 'nps' && (
                  <div className="flex gap-1 mt-3 flex-wrap">
                    {Array.from({ length: q.scale + 1 }, (_, i) => i).map(val => (
                      <button
                        key={val}
                        onClick={() => setAnswers(p => ({ ...p, [q.id]: val }))}
                        className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                          answers[q.id] === val ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-pink-100'
                        }`}
                      >{val}</button>
                    ))}
                    <div className="w-full flex justify-between text-xs text-gray-400 mt-1 px-1">
                      <span>Not likely</span><span>Very likely</span>
                    </div>
                  </div>
                )}
                {q.type === 'text' && (
                  <textarea
                    rows={3}
                    value={answers[q.id] || ''}
                    onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-pink-500"
                    placeholder="Your response..."
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => { setActiveSurvey(null); setAnswers({}); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSubmit} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700 flex items-center gap-2">
              <Send className="w-4 h-4" /> Submit Survey
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Notifications Panel ──────────────────────────────────────────────

const NotificationsPanel = ({ user, notifications, onRefresh, optimization }) => {
  const typeIcons = {
    care_statement_available: FileText,
    care_plan_updated: ClipboardList,
    incident_notification: AlertTriangle,
    activity_photo_shared: Camera,
    new_staff_message: MessageSquare,
  };
  const typeColors = {
    care_statement_available: 'blue',
    care_plan_updated: 'purple',
    incident_notification: 'orange',
    activity_photo_shared: 'green',
    new_staff_message: 'pink',
  };

  const handleMarkAllRead = async () => {
    await FamilyNotificationService.markAllAsRead(user.id);
    onRefresh();
  };

  const mockNotifications = [
    { id: 'n1', type: 'care_plan_updated', title: 'Care Plan Updated', body: 'Quarterly review completed with updated mobility goals.', createdAt: '2026-04-10T08:00:00Z', readAt: null },
    { id: 'n2', type: 'activity_photo_shared', title: 'New Activity Photos', body: '3 new photos from Art Class have been shared.', createdAt: '2026-04-09T14:30:00Z', readAt: null },
    { id: 'n3', type: 'new_staff_message', title: 'New Message from Nurse Kim', body: 'Nurse Kim sent you a message about your mother.', createdAt: '2026-04-09T10:00:00Z', readAt: '2026-04-09T11:00:00Z' },
    { id: 'n4', type: 'care_statement_available', title: 'March Care Statement Available', body: 'The March 2026 care statement is ready for viewing.', createdAt: '2026-04-01T09:00:00Z', readAt: '2026-04-01T10:00:00Z' },
    { id: 'n5', type: 'incident_notification', title: 'Care Update', body: 'A minor care-related event occurred. No injury. Staff addressed promptly.', createdAt: '2026-03-25T16:00:00Z', readAt: '2026-03-25T18:00:00Z' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Notifications</h2>
        <button onClick={handleMarkAllRead} className="text-sm text-pink-600 font-medium hover:underline">Mark all as read</button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
        {mockNotifications.map(notif => {
          const Icon = typeIcons[notif.type] || Bell;
          const color = typeColors[notif.type] || 'gray';
          return (
            <div key={notif.id} className={`p-4 hover:bg-gray-50 transition ${!notif.readAt ? 'bg-pink-50/40' : ''}`}>
              <div className="flex items-start gap-3">
                <div className={`p-2 bg-${color}-100 rounded-lg shrink-0`}>
                  <Icon className={`w-5 h-5 text-${color}-600`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 text-sm">{notif.title}</h4>
                    {!notif.readAt && <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0" />}
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">{notif.body}</p>
                  <span className="text-xs text-gray-400 mt-1 block">{new Date(notif.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Permissions Panel ────────────────────────────────────────────────

const PermissionsPanel = ({ user, optimization }) => {
  const familyMembers = [
    { id: 'FAM-001', name: 'Sarah Johnson', relationship: 'Daughter', email: 'sarah.j@email.com', permission: 'full', status: 'active', mfa: true, lastLogin: '2026-04-10' },
    { id: 'FAM-002', name: 'James Johnson', relationship: 'Son', email: 'james.j@email.com', permission: 'comment', status: 'active', mfa: false, lastLogin: '2026-04-05' },
    { id: 'FAM-003', name: 'Elizabeth Taylor', relationship: 'Granddaughter', email: 'liz.t@email.com', permission: 'view', status: 'pending', mfa: false, lastLogin: null },
  ];

  const visibleData = FamilyPortalService.getVisibleFields('view');
  const permLevels = [
    { level: 'view', desc: 'Can view resident information and updates. Cannot send messages or make changes.' },
    { level: 'comment', desc: 'Can view information and send messages to the care team. Cannot make bookings.' },
    { level: 'full', desc: 'Full access: view, message, book activities, book services, contribute life story, submit feedback.' },
  ];

  const permTooltip = (level) => {
    const match = permLevels.find(p => p.level === level);
    return match ? match.desc : 'Permission level';
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Access & Permissions</h2>

      {/* Invite New */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Family Members with Portal Access</h3>
          <button className="bg-pink-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-pink-700 flex items-center gap-2">
            <Mail className="w-4 h-4" /> Invite Member
          </button>
        </div>
        <div className="space-y-3">
          {familyMembers.map(fm => (
            <div key={fm.id} className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-semibold">
                  {fm.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{fm.name}</span>
                    <span className="text-xs text-gray-500">({fm.relationship})</span>
                    {fm.mfa && <Lock className="w-3 h-3 text-green-600" />}
                  </div>
                  <p className="text-xs text-gray-500">{fm.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Tooltip content={permTooltip(fm.permission)} position="left">
                  <span className={`px-3 py-1 text-xs rounded-full font-medium cursor-help ${
                    fm.permission === 'full' ? 'bg-purple-100 text-purple-700' :
                    fm.permission === 'comment' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{fm.permission}</span>
                </Tooltip>
                <span className={`px-2 py-0.5 text-xs rounded-full ${fm.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{fm.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Permission Levels Explanation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold mb-4">Permission Levels</h3>
        <div className="space-y-3">
          {permLevels.map(p => (
            <div key={p.level} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
              <Shield className="w-5 h-5 text-pink-600 shrink-0 mt-0.5" />
              <div>
                <Tooltip content={`Users with "${p.level}" access have these capabilities`}>
                  <span className="font-medium capitalize cursor-help">{p.level}</span>
                </Tooltip>
                <p className="text-sm text-gray-600">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Visibility */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Eye className="w-5 h-5 text-green-600" /> Visible to Family</h3>
          <ul className="space-y-2">
            {visibleData.visible.map(field => (
              <li key={field} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><X className="w-5 h-5 text-red-600" /> Not Visible</h3>
          <ul className="space-y-2">
            {visibleData.hidden.map(field => (
              <li key={field} className="flex items-center gap-2 text-sm text-gray-700">
                <X className="w-4 h-4 text-red-400" />
                {field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ─── Visit Scheduling Panel ───────────────────────────────────────────

const TIME_SLOTS = [
  { id: 'morning', label: '9:00 AM – 12:00 PM' },
  { id: 'afternoon', label: '1:00 PM – 4:00 PM' },
  { id: 'evening', label: '4:00 PM – 6:00 PM' },
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getWeekDays = (offset) => {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7) + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const isSameDay = (a, b) =>
  a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

const VisitSchedulingPanel = ({ user, optimization }) => {
  const { showToast } = useToast();
  const [bookedVisits, setBookedVisits] = useState([
    { id: 1, date: '2026-04-14', slot: '9:00 AM – 12:00 PM', visitor: 'Sarah Johnson', purpose: 'Birthday celebration', status: 'Confirmed' },
    { id: 2, date: '2026-04-16', slot: '1:00 PM – 4:00 PM', visitor: 'James Johnson', purpose: 'Regular visit', status: 'Pending' },
    { id: 3, date: '2026-04-12', slot: '4:00 PM – 6:00 PM', visitor: 'Elizabeth Taylor', purpose: 'Afternoon tea', status: 'Cancelled' },
  ]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({ visitor: '', purpose: '' });
  const [weekOffset, setWeekOffset] = useState(0);

  const weekDays = getWeekDays(weekOffset);

  const findBooking = (date, slotLabel) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return bookedVisits.find(v => v.date === dateStr && v.slot === slotLabel && v.status !== 'Cancelled');
  };

  const isPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleSlotClick = (date, slot) => {
    if (isPast(date) || findBooking(date, slot.label)) return;
    setSelectedSlot({ date, slot });
    setBookingForm({ visitor: user.name, purpose: '' });
    setShowBookingModal(true);
  };

  const handleBookVisit = () => {
    if (!bookingForm.visitor.trim() || !bookingForm.purpose.trim()) return;
    const dateStr = `${selectedSlot.date.getFullYear()}-${String(selectedSlot.date.getMonth() + 1).padStart(2, '0')}-${String(selectedSlot.date.getDate()).padStart(2, '0')}`;
    const newVisit = {
      id: Date.now(),
      date: dateStr,
      slot: selectedSlot.slot.label,
      visitor: bookingForm.visitor,
      purpose: bookingForm.purpose,
      status: 'Pending',
    };
    setBookedVisits(prev => [...prev, newVisit]);
    setShowBookingModal(false);
    setSelectedSlot(null);
    setBookingForm({ visitor: '', purpose: '' });
    showToast('Visit booked successfully! Awaiting confirmation.', 'success');
    console.info('[FamilyPortal] Visit booked', newVisit);
  };

  const handleCancelVisit = (visitId) => {
    setBookedVisits(prev => prev.map(v => v.id === visitId ? { ...v, status: 'Cancelled' } : v));
    showToast('Visit has been cancelled.', 'info');
    console.info('[FamilyPortal] Visit cancelled', visitId);
  };

  const handleReschedule = (visitId) => {
    handleCancelVisit(visitId);
    showToast('Previous booking cancelled — select a new time slot to reschedule.', 'info');
  };

  const formatDateShort = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

  const statusStyles = {
    Confirmed: 'bg-green-100 text-green-700',
    Pending: 'bg-yellow-100 text-yellow-700',
    Cancelled: 'bg-red-100 text-red-700',
  };

  const activeVisits = bookedVisits
    .filter(v => v.status !== 'Cancelled')
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const cancelledVisits = bookedVisits.filter(v => v.status === 'Cancelled');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-lg font-semibold">Visit Scheduling</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekOffset(p => p - 1)}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition"
          >
            This Week
          </button>
          <button
            onClick={() => setWeekOffset(p => p + 1)}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
            aria-label="Next week"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Weekly Calendar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="p-3 text-left text-xs font-medium text-gray-500 w-32">Time Slot</th>
                {weekDays.map((day, i) => {
                  const today = isSameDay(day, new Date());
                  return (
                    <th key={i} className={`p-3 text-center text-xs font-medium ${today ? 'bg-pink-50 text-pink-700' : 'text-gray-500'}`}>
                      <div>{DAY_NAMES[i]}</div>
                      <div className={`text-sm mt-0.5 ${today ? 'font-bold' : ''}`}>{formatDateShort(day)}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {TIME_SLOTS.map(slot => (
                <tr key={slot.id} className="border-b border-gray-50 last:border-0">
                  <td className="p-3 text-xs font-medium text-gray-600 whitespace-nowrap">{slot.label}</td>
                  {weekDays.map((day, i) => {
                    const booked = findBooking(day, slot.label);
                    const past = isPast(day);
                    const today = isSameDay(day, new Date());
                    return (
                      <td key={i} className={`p-1.5 text-center ${today ? 'bg-pink-50/50' : ''}`}>
                        {booked ? (
                          <div
                            className="bg-pink-100 text-pink-700 text-[10px] rounded-lg p-1.5 font-medium truncate"
                            title={`${booked.visitor} — ${booked.purpose}`}
                          >
                            {booked.visitor.split(' ')[0]}
                          </div>
                        ) : past ? (
                          <div className="bg-gray-50 text-gray-300 text-[10px] rounded-lg p-1.5">—</div>
                        ) : (
                          <button
                            onClick={() => handleSlotClick(day, slot)}
                            className="w-full bg-green-50 hover:bg-green-100 text-green-600 text-[10px] rounded-lg p-1.5 font-medium transition-colors"
                          >
                            Available
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upcoming Visits */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold mb-4">Upcoming Visits</h3>
        {activeVisits.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No upcoming visits scheduled. Click an available slot above to book one.</p>
        ) : (
          <div className="space-y-3">
            {activeVisits.map(visit => (
              <div key={visit.id} className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 bg-pink-100 rounded-lg shrink-0">
                    <Calendar className="w-5 h-5 text-pink-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{visit.visitor}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusStyles[visit.status]}`}>{visit.status}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5 truncate">{visit.purpose}</p>
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(visit.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })} — {visit.slot}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleReschedule(visit.id)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" /> Reschedule
                  </button>
                  <button
                    onClick={() => handleCancelVisit(visit.id)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancelled Visits */}
      {cancelledVisits.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold mb-4 text-gray-500">Cancelled Visits</h3>
          <div className="space-y-2">
            {cancelledVisits.map(visit => (
              <div key={visit.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 opacity-60">
                <div>
                  <span className="text-sm font-medium text-gray-500 line-through">{visit.visitor} — {visit.purpose}</span>
                  <p className="text-xs text-gray-400">{visit.date} · {visit.slot}</p>
                </div>
                <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium">Cancelled</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <Modal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} title="Book a Visit" size="md">
        {selectedSlot && (
          <div className="space-y-4 p-2">
            <div className="bg-pink-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-pink-700 font-medium">
                <Calendar className="w-4 h-4" />
                {selectedSlot.date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              <div className="flex items-center gap-2 text-pink-600 text-sm mt-1">
                <Clock className="w-4 h-4" />
                {selectedSlot.slot.label}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Visitor Name</label>
              <input
                type="text"
                value={bookingForm.visitor}
                onChange={e => setBookingForm(p => ({ ...p, visitor: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Your name..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Purpose of Visit</label>
              <input
                type="text"
                value={bookingForm.purpose}
                onChange={e => setBookingForm(p => ({ ...p, purpose: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="e.g. Regular visit, birthday celebration..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowBookingModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleBookVisit} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Book Visit
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Care Plan Panel ──────────────────────────────────────────────────

const CARE_GOALS = [
  { id: 1, name: 'Mobility', progress: 60, target: 'Walk 50 metres with walking frame daily', summary: 'Maggie is making steady progress with her walking frame. She can now comfortably walk 30 metres and is building confidence each day.', status: 'on_track', updated: '2026-04-08' },
  { id: 2, name: 'Nutrition', progress: 85, target: 'Maintain healthy weight within target range', summary: 'Appetite has been very good lately. She enjoys soft-texture meals and her weight remains stable within the healthy range.', status: 'on_track', updated: '2026-04-10' },
  { id: 3, name: 'Social Engagement', progress: 70, target: 'Participate in 3 or more activities per week', summary: 'Maggie regularly attends art class and music therapy. The team is encouraging more time in the sensory garden.', status: 'on_track', updated: '2026-04-09' },
  { id: 4, name: 'Pain Management', progress: 45, target: 'Keep pain level below 3 out of 10', summary: 'Some discomfort in her left knee has been reported this week. Physiotherapy sessions have been adjusted to help.', status: 'needs_attention', updated: '2026-04-10' },
  { id: 5, name: 'Sleep Quality', progress: 30, target: 'Achieve 7 or more hours of uninterrupted sleep', summary: 'Sleep has been disrupted over the past few nights. The care team is reviewing her evening routine and bedroom environment.', status: 'concern', updated: '2026-04-11' },
];

const CARE_STATUS_CONFIG = {
  on_track: { label: 'On Track', color: 'text-green-700', bg: 'bg-green-100', barColor: 'bg-green-500', dot: 'bg-green-500' },
  needs_attention: { label: 'Needs Attention', color: 'text-amber-700', bg: 'bg-amber-100', barColor: 'bg-amber-500', dot: 'bg-amber-500' },
  concern: { label: 'Concern', color: 'text-red-700', bg: 'bg-red-100', barColor: 'bg-red-500', dot: 'bg-red-500' },
};

const CarePlanPanel = ({ user, optimization }) => {
  const { showToast } = useToast();
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageGoal, setMessageGoal] = useState(null);
  const [messageText, setMessageText] = useState('');

  const handleAskAbout = (goal) => {
    setMessageGoal(goal);
    setMessageText(
      `Hi, I have a question about Maggie's ${goal.name.toLowerCase()} care goal. Could you please provide an update on her progress towards "${goal.target}"?`
    );
    setShowMessageModal(true);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    await FamilyPortalService.sendMessage(user.id, {
      residentId: user.residentId,
      subject: `Question about ${messageGoal.name} care goal`,
      body: messageText,
      toType: 'care_staff',
    });
    setShowMessageModal(false);
    setMessageText('');
    setMessageGoal(null);
    showToast('Your question has been sent to the care team.', 'success');
    console.info('[FamilyPortal] Care plan question sent', messageGoal?.name);
  };

  const overallProgress = Math.round(CARE_GOALS.reduce((sum, g) => sum + g.progress, 0) / CARE_GOALS.length);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Maggie's Care Plan</h2>
        <p className="text-sm text-gray-500 mt-1">A summary of care goals and progress, written in plain language for families.</p>
      </div>

      {/* Overall Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Overall Progress</h3>
          <span className="text-2xl font-bold text-pink-600">{overallProgress}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-pink-500 to-rose-500 h-3 rounded-full transition-all duration-700"
            style={{ width: `${overallProgress}%` }}
            role="progressbar"
            aria-valuenow={overallProgress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Overall care plan progress"
          />
        </div>
        <p className="text-xs text-gray-400 mt-2">Based on {CARE_GOALS.length} active care goals · Last reviewed April 2026</p>
      </div>

      {/* Individual Goals */}
      <div className="space-y-4">
        {CARE_GOALS.map(goal => {
          const config = CARE_STATUS_CONFIG[goal.status];
          return (
            <div key={goal.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{goal.name}</h3>
                    <Tooltip content={`Status: ${config.label}. Target: ${goal.target}`}>
                      <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium ${config.bg} ${config.color} flex items-center gap-1 cursor-help`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
                        {config.label}
                      </span>
                    </Tooltip>
                  </div>

                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                      <div
                        className={`${config.barColor} h-2.5 rounded-full transition-all duration-700`}
                        style={{ width: `${goal.progress}%` }}
                        role="progressbar"
                        aria-valuenow={goal.progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${goal.name} progress`}
                      />
                    </div>
                    <Tooltip content={`${goal.progress}% progress towards: ${goal.target}`}>
                      <span className="text-sm font-semibold text-gray-700 min-w-[3rem] text-right cursor-help">
                        {goal.progress}%
                      </span>
                    </Tooltip>
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed">{goal.summary}</p>

                  <div className="flex items-center gap-4 mt-3 flex-wrap">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Updated {new Date(goal.updated).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Target className="w-3 h-3" /> Goal: {goal.target}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => handleAskAbout(goal)}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-pink-600 border border-pink-200 rounded-lg hover:bg-pink-50 transition"
                >
                  <HelpCircle className="w-4 h-4" />
                  Ask About This
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Status Legend */}
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Status Legend</p>
        <div className="flex flex-wrap gap-4">
          {Object.entries(CARE_STATUS_CONFIG).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
              <span className="text-xs text-gray-600">{cfg.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Ask About Modal */}
      <Modal isOpen={showMessageModal} onClose={() => setShowMessageModal(false)} title="Ask the Care Team" size="md">
        {messageGoal && (
          <div className="space-y-4 p-2">
            <div className="bg-blue-50 rounded-lg p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                Your question about <strong>{messageGoal.name}</strong> will be sent directly to Maggie's care team.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Your Message</label>
              <textarea
                rows={4}
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowMessageModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSendMessage} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700 flex items-center gap-2">
                <Send className="w-4 h-4" /> Send Question
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FamilyPortal;
